/**
 * Customizer state — design system JSON schema, defaults, migrations.
 *
 * Single canonical shape used in three places:
 *   1. IndexedDB (current + history)
 *   2. The customizer's import/export
 *   3. The `{prefix}-design-system.json` file inside every exported zip
 *
 * Round-trip rule: export -> re-import must yield byte-identical state
 * (modulo `updatedAt`).
 */

export const CURRENT_SCHEMA_VERSION = 2;

export interface DesignSystem {
  schemaVersion: number;
  prefix: string;
  updatedAt: string;
  tokens: Tokens;
  overrides: Record<string, string>;
}

export interface Tokens {
  color: ColorTokens;
  type: TypeTokens;
  shape: ShapeTokens;
  depth: DepthTokens;
  surface: SurfaceTokens;
  motion: MotionTokens;
  density: DensityTokens;
  /**
   * Dark-mode derivation. Two tiers:
   *   1. `dark.brandLightness` + `dark.brandChroma` are the principle knobs —
   *      one formula derives every branded palette color's dark variant.
   *   2. `colorDark` is the optional per-color escape hatch — if a key is set,
   *      that value wins for that color and skips the auto-formula.
   * Surface colors (bg, text) always invert lightness regardless of either.
   */
  dark: DarkDerivation;
  colorDark: Partial<ColorTokens>;
  icons: IconTokens;
  brand: BrandTokens;
}

export interface IconTokens {
  set: 'fomantic' | 'lucide';
}

export interface BrandTokens {
  /**
   * Data URL of the light-mode logo (base64-encoded SVG/PNG/etc.).
   * Required: every brand should ship both light and dark variants so it
   * reads cleanly on any background. Exported as /assets/logo-light.{ext}
   * and referenced via --{prefix}-logo-light CSS token.
   */
  logoLight: string;
  /** Data URL of the dark-mode logo. Required — pairs with logoLight. */
  logoDark: string;
  /**
   * Data URL of the favicon icon (square, ideally SVG). Used as the
   * browser tab icon and exported as --{prefix}-favicon.
   */
  icon: string;
}

export interface DarkDerivation {
  brandLightness: number;  // target oklch L for primary/secondary/accent in dark mode (0.5–0.95)
  brandChroma: number;     // chroma multiplier (0–1.5; <1 desaturates, >1 punches up)
}

export interface ColorTokens {
  primary: string;
  secondary: string;
  accent: string;
  bg: string;
  text: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  // Extended palette — Semantic UI's named utility colors. Used for
  // chart palettes, calendar event tags, and `variant="teal|purple|..."`.
  red: string;
  orange: string;
  yellow: string;
  olive: string;
  green: string;
  teal: string;
  blue: string;
  violet: string;
  purple: string;
  pink: string;
  brown: string;
  grey: string;
  black: string;
}

export interface TypeTokens {
  fontDisplay: string;   // emits as --{prefix}-font-display (new derived token)
  fontPrimary: string;   // body text — emits as --{prefix}-font-primary
  fontSecondary: string; // mono / code — emits as --{prefix}-font-secondary
  baseSize: string;      // e.g. "16px"
  scaleRatio: number;    // e.g. 1.25
}

export interface ShapeTokens {
  radiusBase: string;   // e.g. "0.5rem"
  borderWidth: string;  // e.g. "1px"
  outlineWidth: string; // e.g. "0px"
}

export interface DepthTokens {
  shadowColor: string;
  shadowStrength: number;
  shadowSpread: number;
}

/**
 * Surface material — a named surface+edge preset. Owns only the panel fill +
 * border; the radius/shadow it *suggests* are applied to the shape/depth knobs
 * by the customizer (see MATERIAL_SUGGESTIONS), gated by `materialPropagate`.
 * Drives the `data-ry-material` attribute on <html> at runtime.
 */
export type MaterialName = 'fomantic' | 'tailwind';

export interface SurfaceTokens {
  material: MaterialName;     // surface+edge preset (default 'fomantic' = today's look)
  materialPropagate: boolean; // when true, picking a material also applies its suggested radius/shadow
  panelOpacity: number;
  panelBlur: string;    // e.g. "0px"
  useGradients: boolean; // master toggle for gradient backgrounds (default off)
  useWaves: boolean;     // master toggle for the zevo-style wave dividers on heroes (default on)
  /**
   * Wave flourish parameters. Active only when useWaves is true.
   * Each "flourish" feature (waves, gradients, future ones) bundles its
   * own param set; turning the feature on reveals those params in the UI.
   */
  waveSpeed: number;       // animation-duration multiplier (0.25× → 4×). 1 = 45s.
  waveAmplitude: number;   // scale-y of the wave shape (0.1 = flat, 0.6 = tall)
  waveColor: string;       // CSS color or `var(--ry-color-*)` reference
}

export interface MotionTokens {
  animationStyle: 'none' | 'smooth' | 'bouncy';
  durationScale: number;
}

export interface DensityTokens {
  spaceScale: number;
}

/**
 * Default design system — mirrors src/css/ry-tokens.css exactly.
 * Values are zevo's Semantic UI palette (locked 2026-05-22).
 */
export function defaultDesignSystem(prefix = 'ry'): DesignSystem {
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    prefix,
    updatedAt: new Date().toISOString(),
    tokens: {
      color: {
        // Light-mode hex values matching ry-tokens.css. Stored as hex
        // so <input type="color"> can round-trip them directly.
        primary: '#45D684',   // zevo @green
        secondary: '#303641', // zevo @black
        accent: '#7789CF',    // zevo @violet
        bg: '#FFFFFF',
        text: '#212121',      // ≈ rgba(0,0,0,0.87), Semantic UI's @textColor
        success: '#45D684',   // zevo @green
        warning: '#FAB566',   // zevo @yellow
        danger: '#F57C7C',    // zevo @red
        info: '#2095DC',      // zevo @blue
        // Extended palette — full zevo @-variable set
        red: '#F57C7C',
        orange: '#FE8667',
        yellow: '#FAB566',
        olive: '#67E290',
        green: '#45D684',
        teal: '#00AE9E',
        blue: '#2095DC',
        violet: '#7789CF',
        purple: '#C26ED7',
        pink: '#F57CA3',
        brown: '#AC776A',
        grey: '#3B424F',
        black: '#303641',
      },
      type: {
        fontDisplay: "Manrope, Inter, system-ui, sans-serif",
        fontPrimary: "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontSecondary: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
        baseSize: '16px',
        scaleRatio: 1.25,
      },
      shape: {
        radiusBase: '0.5rem',
        borderWidth: '1px',
        outlineWidth: '0px',
      },
      depth: {
        shadowColor: 'oklch(0.27 0.025 258 / 0.08)',
        shadowStrength: 1,
        shadowSpread: 1,
      },
      surface: {
        material: 'fomantic',
        materialPropagate: true,
        panelOpacity: 1,
        panelBlur: '0px',
        useGradients: false,
        useWaves: true,
        waveSpeed: 1,
        waveAmplitude: 0.3,
        // Hex so <ry-color-input> can render the swatch directly.
        // Defaults to the zevo accent color; the user can pick any color or
        // re-point to a brand token via the override system.
        waveColor: '#7789CF',
      },
      motion: {
        animationStyle: 'smooth',
        durationScale: 1,
      },
      density: {
        spaceScale: 1,
      },
      dark: {
        brandLightness: 0.72,
        brandChroma: 0.85,
      },
      colorDark: {},
      icons: {
        set: 'fomantic',
      },
      brand: {
        logoLight: '',
        logoDark: '',
        icon: '',
      },
    },
    overrides: {},
  };
}

/**
 * Migration chain. Given any prior-version JSON, walks it forward to
 * CURRENT_SCHEMA_VERSION. Unknown extra fields are preserved.
 *
 * Each migration takes `(any) => any`. Add new entries as the schema bumps.
 */
const MIGRATIONS: Record<number, (input: any) => any> = {
  // 1 -> 2: drop brand.wordmark, add brand.icon
  1: (v1) => {
    const next = { ...v1, schemaVersion: 2 };
    if (next.tokens?.brand) {
      delete next.tokens.brand.wordmark;
      if (next.tokens.brand.icon === undefined) next.tokens.brand.icon = '';
    }
    return next;
  },
};

export function migrate(input: unknown): DesignSystem {
  if (!input || typeof input !== 'object') {
    return defaultDesignSystem();
  }
  let data: any = { ...(input as object) };

  // Walk forward through migrations
  let v: number = typeof data.schemaVersion === 'number' ? data.schemaVersion : 0;
  while (v < CURRENT_SCHEMA_VERSION) {
    const migrator = MIGRATIONS[v];
    if (!migrator) {
      // Unknown old version — fall back to defaults
      return defaultDesignSystem(typeof data.prefix === 'string' ? data.prefix : 'ry');
    }
    data = migrator(data);
    v = data.schemaVersion;
  }

  // Fill in any missing required fields with defaults (forward-compat insurance)
  const defaults = defaultDesignSystem(typeof data.prefix === 'string' ? data.prefix : 'ry');
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    prefix: data.prefix ?? defaults.prefix,
    updatedAt: data.updatedAt ?? defaults.updatedAt,
    tokens: mergeTokens(defaults.tokens, data.tokens),
    overrides: data.overrides ?? {},
  };
}

function mergeTokens(defaults: Tokens, incoming: any): Tokens {
  if (!incoming || typeof incoming !== 'object') return defaults;
  return {
    color:     { ...defaults.color,     ...(incoming.color     ?? {}) },
    type:      { ...defaults.type,      ...(incoming.type      ?? {}) },
    shape:     { ...defaults.shape,     ...(incoming.shape     ?? {}) },
    depth:     { ...defaults.depth,     ...(incoming.depth     ?? {}) },
    surface:   { ...defaults.surface,   ...(incoming.surface   ?? {}) },
    motion:    { ...defaults.motion,    ...(incoming.motion    ?? {}) },
    density:   { ...defaults.density,   ...(incoming.density   ?? {}) },
    dark:      { ...defaults.dark,      ...(incoming.dark      ?? {}) },
    colorDark: { ...defaults.colorDark, ...(incoming.colorDark ?? {}) },
    icons:     migrateIcons(defaults.icons, incoming.icons),
    brand:     { ...defaults.brand,     ...(incoming.brand     ?? {}) },
  };
}

/**
 * The legacy `'semantic'` icon-set value (Fomantic font icons via external
 * CSS) was removed when the registry collapsed to two visual SVG sets.
 * Old saved state silently coerces to the new default.
 */
function migrateIcons(defaults: IconTokens, incoming: Partial<IconTokens> | undefined): IconTokens {
  const merged = { ...defaults, ...(incoming ?? {}) };
  if ((merged.set as string) !== 'fomantic' && (merged.set as string) !== 'lucide') {
    merged.set = defaults.set;
  }
  return merged;
}

/**
 * Emit a `:root { --{prefix}-...: value; ... }` CSS block from a DesignSystem.
 *
 * Only writes the CORE KNOBS. The derived tokens (color-mix, calc, etc.) are
 * defined in the shipped ry-tokens.css and will recompute from these knob
 * values automatically. Overrides are emitted as a second block so they
 * cascade-win.
 */
export function tokensToCss(ds: DesignSystem, prefix = ds.prefix): string {
  const p = `--${prefix}-`;
  const { color: c, type: t, shape: s, depth: d, surface: sf, motion: m, density: dn, dark: dk, colorDark: cd } = ds.tokens;

  // Dual-mode emission.
  //  - dualSurface: full lightness inversion (white bg <-> dark bg, dark text <-> light text).
  //  - dualBrand:   user-configurable target L + chroma multiplier for branded palette colors.
  // A per-color manual override (cd[key]) always wins if set.
  const dualSurface = (v: string) => `light-dark(${v}, oklch(from ${v} calc(1 - l) c h))`;
  const dualBrand   = (v: string) => `light-dark(${v}, oklch(from ${v} ${dk.brandLightness} calc(c * ${dk.brandChroma}) h))`;
  const dual = (light: string, override: string | undefined, auto: (v: string) => string): string =>
    override ? `light-dark(${light}, ${override})` : auto(light);

  const lines: string[] = [];
  lines.push(':root {');
  // Palette
  lines.push(`  ${p}color-primary: ${dual(c.primary, cd.primary, dualBrand)};`);
  lines.push(`  ${p}color-secondary: ${dual(c.secondary, cd.secondary, dualBrand)};`);
  lines.push(`  ${p}color-accent: ${dual(c.accent, cd.accent, dualBrand)};`);
  lines.push(`  ${p}color-bg: ${dual(c.bg, cd.bg, dualSurface)};`);
  lines.push(`  ${p}color-text: ${dual(c.text, cd.text, dualSurface)};`);
  // Semantic — kept as single value per ry-tokens.css convention ("overrideable, but defaults are universal")
  lines.push(`  ${p}color-success: ${c.success};`);
  lines.push(`  ${p}color-warning: ${c.warning};`);
  lines.push(`  ${p}color-danger: ${c.danger};`);
  lines.push(`  ${p}color-info: ${c.info};`);
  // Extended palette — named utility colors (variant="teal", "purple", etc.)
  lines.push(`  ${p}color-red: ${c.red};`);
  lines.push(`  ${p}color-orange: ${c.orange};`);
  lines.push(`  ${p}color-yellow: ${c.yellow};`);
  lines.push(`  ${p}color-olive: ${c.olive};`);
  lines.push(`  ${p}color-green: ${c.green};`);
  lines.push(`  ${p}color-teal: ${c.teal};`);
  lines.push(`  ${p}color-blue: ${c.blue};`);
  lines.push(`  ${p}color-violet: ${c.violet};`);
  lines.push(`  ${p}color-purple: ${c.purple};`);
  lines.push(`  ${p}color-pink: ${c.pink};`);
  lines.push(`  ${p}color-brown: ${c.brown};`);
  lines.push(`  ${p}color-grey: ${c.grey};`);
  lines.push(`  ${p}color-black: ${c.black};`);
  // Type
  lines.push(`  ${p}font-display: ${t.fontDisplay};`);
  lines.push(`  ${p}font-primary: ${t.fontPrimary};`);
  lines.push(`  ${p}font-secondary: ${t.fontSecondary};`);
  lines.push(`  ${p}text-base: ${t.baseSize};`);
  // Heading scale derives from base + ratio
  const base = parseBaseSize(t.baseSize);
  if (base) {
    lines.push(`  ${p}text-xs: ${(base / (t.scaleRatio * t.scaleRatio)).toFixed(3)}px;`);
    lines.push(`  ${p}text-sm: ${(base / t.scaleRatio).toFixed(3)}px;`);
    lines.push(`  ${p}text-lg: ${(base * t.scaleRatio).toFixed(3)}px;`);
    lines.push(`  ${p}text-xl: ${(base * t.scaleRatio ** 2).toFixed(3)}px;`);
    lines.push(`  ${p}text-2xl: ${(base * t.scaleRatio ** 3).toFixed(3)}px;`);
    lines.push(`  ${p}text-3xl: ${(base * t.scaleRatio ** 4).toFixed(3)}px;`);
    lines.push(`  ${p}text-4xl: ${(base * t.scaleRatio ** 5).toFixed(3)}px;`);
  }
  // Shape
  lines.push(`  ${p}radius-base: ${s.radiusBase};`);
  lines.push(`  ${p}border-width: ${s.borderWidth};`);
  lines.push(`  ${p}outline-width: ${s.outlineWidth};`);
  // Depth
  lines.push(`  ${p}shadow-color: ${d.shadowColor};`);
  lines.push(`  ${p}shadow-strength: ${d.shadowStrength};`);
  lines.push(`  ${p}shadow-spread: ${d.shadowSpread};`);
  // Surface
  lines.push(`  ${p}panel-opacity: ${sf.panelOpacity};`);
  lines.push(`  ${p}panel-blur: ${sf.panelBlur};`);
  lines.push(`  ${p}use-gradients: ${sf.useGradients ? 1 : 0};`);
  lines.push(`  ${p}use-waves: ${sf.useWaves ? 1 : 0};`);
  // Wave flourish params (active when use-waves is 1)
  lines.push(`  ${p}wave-speed: ${sf.waveSpeed};`);
  lines.push(`  ${p}wave-amplitude: ${sf.waveAmplitude};`);
  lines.push(`  ${p}wave-color: ${sf.waveColor};`);
  // Motion
  lines.push(`  ${p}animation-style: ${m.animationStyle};`);
  lines.push(`  ${p}duration-scale: ${m.durationScale};`);
  // Density
  lines.push(`  ${p}space-scale: ${dn.spaceScale};`);
  lines.push('}');

  // Overrides — cascade after derived definitions in the shipped CSS
  const overrideKeys = Object.keys(ds.overrides);
  if (overrideKeys.length > 0) {
    lines.push('');
    lines.push('/* Manual overrides */');
    lines.push(':root {');
    for (const key of overrideKeys) {
      lines.push(`  --${prefix}-${key}: ${ds.overrides[key]};`);
    }
    lines.push('}');
  }

  // Brand asset tokens — only emit when the user has uploaded something.
  // url() wrapping handles both data: URLs and relative /assets/ paths.
  const { brand: b } = ds.tokens;
  if (b.logoLight || b.logoDark || b.icon) {
    lines.push(':root {');
    if (b.logoLight) lines.push(`  ${p}logo-light: url("${b.logoLight}");`);
    if (b.logoDark)  lines.push(`  ${p}logo-dark: url("${b.logoDark}");`);
    // --{prefix}-logo resolves to the right variant via light-dark() if both
    // are set; falls back to whichever exists.
    if (b.logoLight && b.logoDark) {
      lines.push(`  ${p}logo: light-dark(var(${p}logo-light), var(${p}logo-dark));`);
    } else if (b.logoLight) {
      lines.push(`  ${p}logo: var(${p}logo-light);`);
    } else if (b.logoDark) {
      lines.push(`  ${p}logo: var(${p}logo-dark);`);
    }
    if (b.icon) lines.push(`  ${p}favicon: url("${b.icon}");`);
    lines.push('}');
  }

  return lines.join('\n');
}

function parseBaseSize(value: string): number | null {
  const m = value.match(/^([\d.]+)(px|rem)?$/);
  if (!m) return null;
  const n = parseFloat(m[1]!);
  if (Number.isNaN(n)) return null;
  return m[2] === 'rem' ? n * 16 : n;
}
